import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getPagedAuditLogs,
  filterAuditLogsByDateRange,
} from '../../../../services/auditLogService';
import Drawer from '../../../../components/Drawer/Drawer';
import { useLanguage } from '../../../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../../../utils/createScopedI18n';
import { auditLogI18n } from './i18n';
import { useSnackbar } from '../../../../context/SnackbarContext';
import './AuditLog.css';

const SORT_KEY_MAP = {
  date: 'date',
  user: 'user',
  action: 'action',
};

const DEFAULT_START_DATE = '1900-01-01T00:00:00';
const DEFAULT_END_DATE = '2100-12-31T23:59:59';

const formatDate = (value, locale = 'es-CL') => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString(locale);
};

const parseJSON = (value) => {
  if (!value) return null;
  if (typeof value === 'object') return value;
  if (typeof value !== 'string') return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const getIpParts = (row) => {
  const raw = row?.ipAddress ?? row?.ip ?? '';
  if (!raw) return { flag: '', ip: '' };
  const parts = String(raw).split('\r\n');
  if (parts.length > 1) {
    return { flag: parts[0] ?? '', ip: parts[1] ?? '' };
  }
  return { flag: '', ip: raw };
};

const formatArrayItem = (item) => {
  if (item === null || item === undefined) return '-';
  if (typeof item === 'object') {
    return (
      item.name ??
      item.userName ??
      item.nombre ??
      item.id ??
      item.userId ??
      item.usuarioId ??
      JSON.stringify(item)
    );
  }
  return String(item);
};

const formatChangeValue = (value) => {
  if (value === null || value === undefined) return '-';
  if (Array.isArray(value)) {
    if (!value.length) return '-';
    return value.map(formatArrayItem).join(', ');
  }
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const formatFilterValue = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (Array.isArray(value)) {
    return value.length ? value.map(formatArrayItem).join(', ') : null;
  }
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const buildFilterSummary = (data) => {
  if (!data || typeof data !== 'object') return '-';
  const entries = Object.entries(data)
    .map(([key, value]) => {
      const formatted = formatFilterValue(value);
      if (!formatted) return null;
      return `${key}: ${formatted}`;
    })
    .filter(Boolean);
  return entries.length ? entries.join(' | ') : '-';
};

const getUserKey = (entry) => {
  if (entry && typeof entry === 'object') {
    return (
      entry.id ??
      entry.userId ??
      entry.usuarioId ??
      entry.userid ??
      JSON.stringify(entry)
    );
  }
  return entry;
};

const getUserLabel = (entry) => {
  if (entry && typeof entry === 'object') {
    return (
      entry.name ??
      entry.userName ??
      entry.nombre ??
      entry.email ??
      entry.id ??
      entry.userId ??
      entry.usuarioId ??
      JSON.stringify(entry)
    );
  }
  return entry;
};

export default function AuditLog() {
  const { language } = useLanguage();
  const auditLogLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: auditLogI18n });
  const { showSnackbar } = useSnackbar();
  const { DesignSystemComponents } = useOutletContext() ?? {};
  const { Card, Button, Input, Table, IconButton } =
    DesignSystemComponents ?? {};

  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [tableKey, setTableKey] = useState(0);
  const [sortKey, setSortKey] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isFiltered, setIsFiltered] = useState(false);
  const [search, setSearch] = useState('');
  const [searchApplied, setSearchApplied] = useState('');

  useEffect(() => {
    auditLogI18n.changeLanguage(auditLogLanguage);
  }, [auditLogLanguage]);

  const backendSortKey = useMemo(
    () => SORT_KEY_MAP[sortKey] || 'date',
    [sortKey]
  );
  const backendSortDesc = useMemo(
    () => (sortDirection === null ? true : sortDirection === 'desc'),
    [sortDirection]
  );

  const currentLocale = useMemo(() => {
    if (auditLogLanguage === 'enUS') return 'en-US';
    if (auditLogLanguage === 'ptBR') return 'pt-BR';
    return 'es-CL';
  }, [auditLogLanguage]);

  const fetchAuditLogs = useCallback(async () => {
    try {
      setLoading(true);
      let response;

      if (isFiltered && (startDate || endDate)) {
        const sd = startDate ? `${startDate}T00:00:00` : DEFAULT_START_DATE;
        const ed = endDate ? `${endDate}T23:59:59` : DEFAULT_END_DATE;
        response = await filterAuditLogsByDateRange(
          sd,
          ed,
          page + 1,
          pageSize,
          searchApplied,
          backendSortKey,
          backendSortDesc
        );
      } else {
        response = await getPagedAuditLogs(
          page + 1,
          pageSize,
          searchApplied,
          backendSortKey,
          backendSortDesc
        );
      }

      setRows(response.data);
      setTotal(response.total);
    } catch (error) {
      console.error('Error loading audit logs:', error);
      showSnackbar(error.customMessage || t('audit_log_error_load'), 'error');
    } finally {
      setLoading(false);
    }
  }, [
    isFiltered,
    startDate,
    endDate,
    page,
    pageSize,
    searchApplied,
    backendSortKey,
    backendSortDesc,
    showSnackbar,
    t,
  ]);

  useEffect(() => {
    fetchAuditLogs();
  }, [fetchAuditLogs]);

  const handleServerSortChange = ({ sortKey: key, direction }) => {
    const normalizedDirection =
      typeof direction === 'string' ? direction.toLowerCase() : direction;
    setSortKey(normalizedDirection ? key : null);
    setSortDirection(normalizedDirection ?? null);
    setPage(0);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(0);
    setTableKey((prev) => prev + 1);
  };

  const onFilter = () => {
    setPage(0);
    setIsFiltered(true);
  };

  const onClearFilter = () => {
    setStartDate('');
    setEndDate('');
    setSearch('');
    setSearchApplied('');
    setPage(0);
    setIsFiltered(false);
  };

  const onSearch = () => {
    setPage(0);
    setSearchApplied(search);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      onSearch();
    }
  };

  const openDrawer = (row) => {
    setSelectedRow(row);
    setIsDrawerOpen(true);
  };

  const closeDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedRow(null);
  };

  const getTargetData = (row) =>
    parseJSON(row?.target ?? row?.object ?? row?.objeto);

  const getPayloadData = (row) =>
    parseJSON(row?.payloadJson ?? row?.payload ?? row?.payloadjson);

  const getRowTime = (row) => {
    const raw =
      row?.date ??
      row?.timestamp ??
      row?.create_At ??
      row?.createdAt ??
      row?.created_at ??
      row?.createAt ??
      row?.update_At ??
      null;
    if (!raw) return null;
    const parsed = Date.parse(raw);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const getDeletedFlag = (row) => {
    if (row?.deleted === true || row?.deleted === false) return row.deleted;
    if (row?.isDeleted === true || row?.isDeleted === false)
      return row.isDeleted;
    if (row?.isdeleted === true || row?.isdeleted === false)
      return row.isdeleted;

    const action = String(row?.action ?? '')
      .trim()
      .toLowerCase();
    if (action.includes('elimin') || action.includes('borr')) return true;
    if (action.includes('activ')) return false;
    return null;
  };

  const getTargetIdentity = (row) => {
    const target = getTargetData(row);
    if (!target) return null;

    const protocolId = target.protocolid ?? target.protocolId ?? null;
    if (protocolId) return `protocol:${protocolId}`;

    const taskId =
      target.taskid ??
      target.taskId ??
      target.taskconfigid ??
      target.taskConfigId ??
      null;
    if (taskId) return `task:${taskId}`;

    const courtId =
      target.courtid ??
      target.courtId ??
      target.tribunalid ??
      target.tribunalId ??
      null;
    if (courtId) return `court:${courtId}`;

    const serviceId = target.serviceid ?? target.serviceId ?? null;
    if (serviceId) return `service:${serviceId}`;

    const areaId =
      target.businessareaid ??
      target.businessAreaId ??
      target.areaid ??
      target.areaId ??
      null;
    if (areaId) return `area:${areaId}`;

    const businessLineId =
      target.businesslineid ??
      target.businessLineId ??
      target.negocioid ??
      target.negocioId ??
      null;
    if (businessLineId) return `businessLine:${businessLineId}`;

    const documentId =
      target.documentid ??
      target.documentId ??
      target.documentconfigurationid ??
      target.documentConfigurationId ??
      null;
    if (documentId) return `document:${documentId}`;

    const branchId = target.branchid ?? target.branchId ?? null;
    if (branchId) return `branch:${branchId}`;

    const excludedId =
      target.excludedid ??
      target.excludedId ??
      target.excludedplaintiffid ??
      target.excludedPlaintiffId ??
      target.excludedplaintiffsaasid ??
      target.excludedPlaintiffSaasId ??
      null;
    if (excludedId) return `excluded:${excludedId}`;

    return null;
  };

  const latestDeletedByTarget = useMemo(() => {
    const map = new Map();
    rows.forEach((row, index) => {
      const key = getTargetIdentity(row);
      if (!key) return;
      const deleted = getDeletedFlag(row);
      if (deleted === null) return;
      const time = getRowTime(row);
      const existing = map.get(key);
      if (!existing) {
        map.set(key, { deleted, time, index });
        return;
      }
      const isNewer =
        time !== null && existing.time !== null
          ? time > existing.time
          : time !== null && existing.time === null
            ? true
            : time === null && existing.time !== null
              ? false
              : index < existing.index;
      if (isNewer) {
        map.set(key, { deleted, time, index });
      }
    });
    return map;
  }, [rows]);

  const getTargetDisplay = (row) => {
    const action = String(row?.action ?? '')
      .trim()
      .toLowerCase();
    const isDownloadAction = action.startsWith('descarga');
    const targetTypeRaw =
      row?.targetType ?? row?.targettype ?? row?.tipoObjetivo ?? '';
    const targetType = String(targetTypeRaw).trim().toLowerCase();
    if (targetType === 'roleoautomatico') {
      if (isDownloadAction) {
        const filters = getTargetData(row) ?? getPayloadData(row);
        const summary = buildFilterSummary(filters);
        return {
          label: summary || '-',
          to: null,
          state: null,
        };
      }
      const payloadTarget = getTargetData(row) ?? getPayloadData(row);
      const userLabel =
        payloadTarget?.name ??
        payloadTarget?.username ??
        payloadTarget?.userName ??
        payloadTarget?.email ??
        payloadTarget?.userid ??
        payloadTarget?.userId ??
        t('audit_log_user_fallback');
      return {
        label: userLabel,
        to: '/configuracion/roleo-automatico',
        state: { search: userLabel },
      };
    }
    if (targetType === 'usuario' || targetType === 'user') {
      const payloadTarget = getTargetData(row) ?? getPayloadData(row);
      const userLabel =
        payloadTarget?.name ??
        payloadTarget?.userName ??
        payloadTarget?.username ??
        payloadTarget?.email ??
        payloadTarget?.userid ??
        payloadTarget?.userId ??
        t('audit_log_user_fallback');
      return {
        label: userLabel,
        to: '/configuracion/usuarios',
        state: { search: userLabel },
      };
    }
    if (targetType === 'enviodocumento') {
      const payloadTarget = getTargetData(row) ?? getPayloadData(row);
      const label =
        payloadTarget?.name ??
        payloadTarget?.documentName ??
        payloadTarget?.nombre ??
        t('audit_log_doc_delivery_fallback');
      return {
        label,
        to: '/smart-docs/registro-documentos',
        state: { search: label },
      };
    }
    if (isDownloadAction) {
      const filters = getTargetData(row) ?? getPayloadData(row);
      return { label: buildFilterSummary(filters), to: null, state: null };
    }

    if (action === 'activación' || action === 'activacion') {
      const activationTarget = getTargetData(row) ?? getPayloadData(row);
      const clientId =
        activationTarget?.clientId ?? activationTarget?.clientid ?? null;
      const label =
        activationTarget?.clientIdentifier ??
        activationTarget?.identifier ??
        activationTarget?.rut ??
        activationTarget?.clientName ??
        activationTarget?.name ??
        (clientId
          ? t('audit_log_client_with_id', { id: clientId })
          : t('audit_log_client_fallback'));
      return {
        label,
        to: clientId ? `/crm/detalles-cliente/${clientId}` : null,
        state: null,
      };
    }

    if (action === 'analisis') {
      const analysisTarget = getTargetData(row) ?? getPayloadData(row);
      const logProtocolId = analysisTarget?.logprotocolid ?? null;
      if (logProtocolId) {
        const protocolName =
          analysisTarget?.protocol ??
          t('audit_log_analysis_fallback', { id: logProtocolId });
        return {
          label: protocolName,
          to: '/ia/protocolos',
          state: { logProtocolId },
        };
      }
    }

    const target = getTargetData(row);
    if (!target) {
      return { label: row?.object ?? '', to: null, state: null };
    }

    const protocolId = target.protocolid ?? target.protocolId ?? null;
    if (protocolId) {
      const protocolName =
        target.nombre ??
        target.name ??
        t('audit_log_protocol_fallback', { id: protocolId });
      return {
        label: protocolName,
        to: '/configuracion/protocolo',
        state: { protocolIdToOpen: protocolId },
      };
    }

    const taskId =
      target.taskid ??
      target.taskId ??
      target.taskconfigid ??
      target.taskConfigId ??
      null;
    if (taskId) {
      const taskLabel = t('audit_log_task_fallback', { id: taskId });
      return {
        label: taskLabel,
        to: '/configuracion/gestion-datos/tareas',
        state: { search: String(taskId) },
      };
    }

    const courtId =
      target.courtid ??
      target.courtId ??
      target.tribunalid ??
      target.tribunalId ??
      null;
    if (courtId || target.courtName || target.tribunal || target.nombre) {
      const courtLabel =
        target.courtName ??
        target.tribunal ??
        target.nombre ??
        target.name ??
        t('audit_log_court_fallback', { id: courtId ?? '' }).trim();
      return {
        label: courtLabel,
        to: '/configuracion/gestion-datos/tribunales',
        state: { search: courtLabel },
      };
    }

    const serviceId = target.serviceid ?? target.serviceId ?? null;
    if (serviceId || target.serviceName || target.servicio || target.nombre) {
      const serviceLabel =
        target.serviceName ??
        target.servicio ??
        target.nombre ??
        target.name ??
        t('audit_log_service_fallback', { id: serviceId ?? '' }).trim();
      return {
        label: serviceLabel,
        to: '/configuracion/gestion-datos/servicios',
        state: { search: serviceLabel },
      };
    }

    const areaId =
      target.businessareaid ??
      target.businessAreaId ??
      target.areaid ??
      target.areaId ??
      null;
    if (areaId || target.businessArea || target.area) {
      const areaLabel =
        target.businessareaname ??
        target.businessAreaName ??
        target.businessArea ??
        target.area ??
        target.nombre ??
        target.name ??
        t('audit_log_area_fallback', { id: areaId ?? '' }).trim();
      return {
        label: areaLabel,
        to: '/configuracion/gestion-datos/areas',
        state: { search: areaLabel },
      };
    }

    const excludedId =
      target.excludedid ??
      target.excludedId ??
      target.excludedplaintiffid ??
      target.excludedPlaintiffId ??
      target.excludedplaintiffsaasid ??
      target.excludedPlaintiffSaasId ??
      null;
    if (excludedId || target.excluded || target.excludedName) {
      const excludedLabel =
        target.excludedName ??
        target.excluded ??
        target.nombre ??
        target.name ??
        t('audit_log_excluded_fallback', { id: excludedId ?? '' }).trim();
      return {
        label: excludedLabel,
        to: '/configuracion/gestion-datos/excluidos',
        state: { search: excludedLabel },
      };
    }

    const businessLineId =
      target.businesslineid ??
      target.businessLineId ??
      target.negocioid ??
      target.negocioId ??
      null;
    if (businessLineId || target.businessLine || target.lineaNegocio) {
      const businessLabel =
        target.businessLine ??
        target.lineaNegocio ??
        target.name ??
        target.nombre ??
        t('audit_log_line_fallback', { id: businessLineId ?? '' }).trim();
      return {
        label: businessLabel,
        to: '/configuracion/gestion-datos/negocios',
        state: { search: businessLabel },
      };
    }

    const documentId =
      target.documentid ??
      target.documentId ??
      target.documentconfigurationid ??
      target.documentConfigurationId ??
      null;
    if (documentId || target.documentName || target.name) {
      const documentLabel =
        target.name ??
        target.documentName ??
        target.nombre ??
        t('audit_log_document_fallback', { id: documentId ?? '' }).trim();
      return {
        label: documentLabel,
        to: '/configuracion/envio-documentos',
        state: { search: documentLabel },
      };
    }

    const caseId = target.caseid ?? target.caseId ?? null;
    const caseCode = target.casecode ?? target.caseCode ?? null;
    const caseTitle = target.casetitle ?? target.caseTitle ?? null;
    const caseDisplay = target.casedisplay ?? target.caseDisplay ?? null;
    if (caseId || caseCode || caseTitle || caseDisplay) {
      return {
        label: caseDisplay
          ? t('audit_log_case_prefix', { value: caseDisplay })
          : caseCode
            ? t('audit_log_case_prefix', { value: caseCode })
            : (caseTitle ?? t('audit_log_case_fallback', { id: caseId ?? '' })),
        to: caseId ? `/detalle-causa/${caseId}` : null,
        state: null,
      };
    }

    const clientId = target.clientid ?? target.clientId ?? null;
    const clientLabel =
      target.name ??
      (target.identifier
        ? t('audit_log_client_with_identifier', {
            identifier: target.identifier,
          })
        : null) ??
      (target.rut
        ? t('audit_log_client_with_identifier', { identifier: target.rut })
        : null) ??
      (clientId
        ? t('audit_log_client_with_id', { id: clientId })
        : t('audit_log_client_fallback'));
    return {
      label: clientLabel,
      to: clientId ? `/crm/detalles-cliente/${clientId}` : null,
      state: null,
    };
  };

  const isDeletedRow = (row) =>
    row?.deleted === true || row?.isDeleted === true || row?.isdeleted === true;

  const renderTargetLink = (row) => {
    const { label, to, state } = getTargetDisplay(row);
    if (!label) return '';
    const targetKey = getTargetIdentity(row);
    const latest = targetKey ? latestDeletedByTarget.get(targetKey) : null;
    const isDeleted =
      latest?.deleted === true
        ? true
        : latest?.deleted === false
          ? false
          : isDeletedRow(row);
    const allowInactiveRedirect =
      targetKey?.startsWith('court:') ||
      targetKey?.startsWith('businessLine:') ||
      targetKey?.startsWith('service:') ||
      targetKey?.startsWith('area:') ||
      targetKey?.startsWith('excluded:');
    if (isDeleted && allowInactiveRedirect && to) {
      return (
        <Link
          to={to}
          state={{ ...(state ?? {}), status: 'Inactivos' }}
          className='auditlog-link-deleted'
        >
          {label}
        </Link>
      );
    }
    if (isDeleted) {
      return <span className='auditlog-link-deleted'>{label}</span>;
    }
    if (!to) return label;
    return (
      <Link to={to} state={state} className='auditlog-link'>
        {label}
      </Link>
    );
  };

  const getActionLabel = (row) => {
    const action = String(row?.action ?? '').trim();
    const targetTypeRaw =
      row?.targetType ?? row?.targettype ?? row?.tipoObjetivo ?? '';
    const targetType = String(targetTypeRaw).trim().toLowerCase();
    if (targetType === 'roleoautomatico') {
      return action;
    }
    return action;
  };

  const getBeforeAfterData = (row) => ({
    before: parseJSON(row?.beforeJson) ?? null,
    after: parseJSON(row?.afterJson) ?? null,
  });

  const buildAssignmentSummary = (row) => {
    const target = getTargetData(row);
    if (!target) return null;

    const taskId = target.taskid ?? target.taskId ?? null;
    if (taskId) {
      const caseCode =
        target.casedisplay ??
        target.caseDisplay ??
        target.casecode ??
        target.caseCode ??
        null;
      const caseId = target.caseid ?? target.caseId ?? null;
      const taskLabel = target.taskname ?? target.taskName ?? taskId;
      if (!caseCode || !caseId) return null;
      return { type: 'task', taskLabel, caseCode, caseId, taskId };
    }

    const { before: beforeData, after: afterData } = getBeforeAfterData(row);
    const beforeUsers = Array.isArray(beforeData?.usuariosAsignados)
      ? beforeData.usuariosAsignados
      : [];
    const afterUsers = Array.isArray(afterData?.usuariosAsignados)
      ? afterData.usuariosAsignados
      : [];
    const beforeKeys = new Set(beforeUsers.map(getUserKey));
    const addedUsers = afterUsers.filter(
      (entry) => !beforeKeys.has(getUserKey(entry))
    );
    if (!addedUsers.length) return null;

    const userName =
      target.name ?? target.userName ?? getUserLabel(addedUsers[0]) ?? null;
    const caseCode =
      target.casecode ?? target.caseCode ?? target.codigo ?? null;
    const caseId = target.caseid ?? target.caseId ?? target.id ?? null;
    if (!userName || !caseCode) return null;
    return { type: 'user', userName, caseCode, caseId };
  };

  const renderChangeEntries = (data) => {
    if (!data || typeof data !== 'object') {
      return <span className='color-gray-600'>{t('audit_log_no_data')}</span>;
    }
    const entries = Object.entries(data);
    if (entries.length === 0) {
      return <span className='color-gray-600'>{t('audit_log_no_data')}</span>;
    }
    return entries.map(([key, value]) => (
      <div key={key} className='mb-2'>
        <div className='auditlog-label'>{key}</div>
        <div className='auditlog-value'>{formatChangeValue(value)}</div>
      </div>
    ));
  };

  const ipParts = useMemo(() => getIpParts(selectedRow), [selectedRow]);
  const assignmentSummary = useMemo(
    () => buildAssignmentSummary(selectedRow),
    [selectedRow]
  );
  const payloadData = useMemo(() => getPayloadData(selectedRow), [selectedRow]);
  const { before: beforeData, after: afterData } = useMemo(
    () => getBeforeAfterData(selectedRow),
    [selectedRow]
  );

  if (!Card || !Button || !Input || !Table || !IconButton) return null;

  return (
    <div lang={currentLocale}>
      <Card specialPadding='p-4'>
        <div className='mb-3'>
          <h2 className='mb-1'>{t('audit_log_title')}</h2>
          <p className='caption font-light'>{t('audit_log_subtitle')}</p>
        </div>

        <div className='d-flex align-items-center gap-3 mb-3 flex-wrap'>
          <div style={{ minWidth: 280 }}>
            <label className='caption d-block mb-1'>
              {t('audit_log_search_label')}
            </label>
            <div className='d-flex gap-2'>
              <Input
                type='text'
                name='auditSearch'
                placeholder={t('audit_log_search_placeholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyPress={handleSearchKeyPress}
              />
              <Button
                label={t('audit_log_search_button')}
                size='sm'
                variant='primary'
                onClick={onSearch}
              />
            </div>
          </div>
          <div style={{ minWidth: 220 }}>
            <label className='caption d-block mb-1'>
              {t('audit_log_start_date')}
            </label>
            <Input
              type='date'
              name='auditStart'
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              lang={currentLocale}
            />
          </div>
          <div style={{ minWidth: 220 }}>
            <label className='caption d-block mb-1'>
              {t('audit_log_end_date')}
            </label>
            <Input
              type='date'
              name='auditEnd'
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              lang={currentLocale}
            />
          </div>
          <div style={{ marginTop: '0px' }}>
            <Button
              label={t('audit_log_filter_button')}
              size='sm'
              variant='primary'
              onClick={onFilter}
            />
          </div>
          {(isFiltered || searchApplied) && (
            <div style={{ marginTop: '0px' }}>
              <Button
                label={t('audit_log_clear_button')}
                size='sm'
                variant='secondary'
                onClick={onClearFilter}
              />
            </div>
          )}
        </div>

        <div
          className='table-container table-auditLog'
          style={{ fontSize: '13px' }}
        >
          <style>{`
          .table-container table {
            font-size: 13px !important;
          }
          .table-container th,
          .table-container td {
            font-size: 13px !important;
            padding: 8px 12px !important;
          }
        `}</style>
          <Table
            key={`table-${tableKey}-${pageSize}`}
            columns={[
              {
                accessor: 'date',
                label: t('audit_log_col_date'),
                sort: true,
                render: (row) => formatDate(row.date, currentLocale),
              },
              { accessor: 'user', label: t('audit_log_col_user'), sort: true },
              {
                accessor: 'action',
                label: t('audit_log_col_action'),
                sort: true,
                render: (row) => getActionLabel(row),
              },
              {
                accessor: 'object',
                label: t('audit_log_col_object'),
                render: (row) => renderTargetLink(row),
              },
              {
                label: '',
                render: (row) => (
                  <IconButton
                    ariaLabel={t('audit_log_view_detail_aria')}
                    icon='eye'
                    size='sm'
                    onClick={() => openDrawer(row)}
                  />
                ),
              },
            ]}
            data={rows}
            page={page}
            onPageChange={setPage}
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
            paginationMode='server'
            totalItems={total}
            paginator
            serverSortKey={sortKey}
            serverSortDirection={sortDirection}
            onServerSortChange={handleServerSortChange}
            loading={loading}
            paginatorGroup={[
              { label: '10', value: 10 },
              { label: '20', value: 20 },
              { label: '50', value: 50 },
              { label: '100', value: 100 },
            ]}
          />
          {!loading && rows.length === 0 && (
            <div className='text-center p-4'>
              <p className='caption font-light'>{t('audit_log_no_records')}</p>
            </div>
          )}
        </div>
      </Card>

      <Drawer
        isOpen={isDrawerOpen}
        title={t('audit_log_drawer_title')}
        onClose={closeDrawer}
        IconButton={IconButton}
      >
        {selectedRow ? (
          <div className='auditlog-drawer'>
            <div className='auditlog-section-title'>
              {t('audit_log_section_general')}
            </div>
            <div className='row g-3 mb-2'>
              <div className='col-6'>
                <div className='auditlog-label'>
                  {t('audit_log_label_date')}
                </div>
                <div className='auditlog-value'>
                  {formatDate(
                    selectedRow.date ??
                      selectedRow.timestamp ??
                      selectedRow.timestampISO,
                    currentLocale
                  ) || '-'}
                </div>
              </div>
              <div className='col-6'>
                <div className='auditlog-label'>
                  {t('audit_log_label_user')}
                </div>
                <div className='auditlog-value'>{selectedRow.user ?? '-'}</div>
              </div>
              <div className='col-6'>
                <div className='auditlog-label'>
                  {t('audit_log_label_action')}
                </div>
                <div className='auditlog-value'>
                  {getActionLabel(selectedRow) || '-'}
                </div>
              </div>
              <div className='col-6'>
                <div className='auditlog-label'>
                  {t('audit_log_label_object')}
                </div>
                <div className='auditlog-value'>
                  {renderTargetLink(selectedRow) || '-'}
                </div>
              </div>
              <div className='col-6'>
                <div className='auditlog-label'>{t('audit_log_label_ip')}</div>
                <div className='auditlog-value d-flex align-items-center gap-2'>
                  {ipParts.flag ? <span>{ipParts.flag}</span> : null}
                  <span>{ipParts.ip || '-'}</span>
                </div>
              </div>
            </div>

            <div className='auditlog-section-title'>
              {t('audit_log_section_changes')}
            </div>
            {assignmentSummary?.type === 'task' ? (
              <div className='auditlog-summary'>
                {t('audit_log_assigned_task_prefix', {
                  task: assignmentSummary.taskLabel,
                })}{' '}
                {assignmentSummary.caseId ? (
                  <Link
                    to={`/detalle-causa/${assignmentSummary.caseId}/tareas`}
                    state={{ taskIdToOpen: assignmentSummary.taskId }}
                    className='auditlog-link'
                  >
                    {assignmentSummary.caseCode}
                  </Link>
                ) : (
                  assignmentSummary.caseCode
                )}
              </div>
            ) : assignmentSummary?.type === 'user' ? (
              <div className='auditlog-summary'>
                {t('audit_log_assigned_user_prefix', {
                  user: assignmentSummary.userName,
                })}{' '}
                {assignmentSummary.caseId ? (
                  <Link
                    to={`/detalle-causa/${assignmentSummary.caseId}`}
                    className='auditlog-link'
                  >
                    {assignmentSummary.caseCode}
                  </Link>
                ) : (
                  assignmentSummary.caseCode
                )}
              </div>
            ) : null}
            <div className='row g-3 mb-2'>
              <div className='col-6'>
                <div className='d-flex align-items-center gap-2 mb-2'>
                  <span className='auditlog-dot auditlog-dot-before' />
                  <span className='auditlog-subtitle'>
                    {t('audit_log_before')}
                  </span>
                </div>
                <div className='auditlog-change-card'>
                  {renderChangeEntries(beforeData)}
                </div>
              </div>
              <div className='col-6'>
                <div className='d-flex align-items-center gap-2 mb-2'>
                  <span className='auditlog-dot auditlog-dot-after' />
                  <span className='auditlog-subtitle'>
                    {t('audit_log_after')}
                  </span>
                </div>
                <div className='auditlog-change-card'>
                  {renderChangeEntries(afterData)}
                </div>
              </div>
            </div>

            <div className='auditlog-section-title'>
              {t('audit_log_section_json')}
            </div>
            <pre className='auditlog-json'>
              {JSON.stringify(
                payloadData ?? selectedRow?.payloadJson ?? {},
                null,
                2
              )}
            </pre>
          </div>
        ) : null}
      </Drawer>
    </div>
  );
}
